import { useEffect, useState } from "react";
import { Page } from "./Components/Page";

interface MigrationPageProps {
  title: React.ReactNode;
  pageType: string;
  path?: string; // consumed by Reach Router for routing, not used by this component
}

// Staging has no gateway rule to intercept the reload below and hand it to
// tinuiti-frontend, so reloading there would just loop forever. Skip the reload and show
// a static message instead.
const migrationFallbackEnabled = (): boolean =>
  process.env.REACT_APP_MIGRATION_PAGE_FALLBACK === "true";

// A path the gateway doesn't route to tinuiti-frontend comes straight back here after the reload,
// so reload at most once per path. The key holds a timestamp rather than a flag: a later in-app
// visit to the same (correctly routed) path must still reload. The key is never removed: a remount
// before the page unloads would otherwise consume it and the reload would loop.
const RELOAD_LOOP_WINDOW_MS = 10_000;

// Set once reload() is queued, so a remount before the page unloads doesn't run the guard again.
let reloading = false;

const reloadedRecently = (): boolean => {
  const key = `migrationPageReload:${window.location.pathname}`;
  const lastReload = Number(sessionStorage.getItem(key));
  if (Date.now() - lastReload < RELOAD_LOOP_WINDOW_MS) {
    return true;
  }
  sessionStorage.setItem(key, String(Date.now()));
  return false;
};

export const MigrationPage = ({ title, pageType }: MigrationPageProps): JSX.Element => {
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      throw new Error("Page has been migrated to tinuiti-frontend");
    } else if (!migrationFallbackEnabled() && !reloading) {
      if (reloadedRecently()) {
        setNotFound(true);
      } else {
        reloading = true;
        window.location.reload();
      }
    }
  }, []);

  if (notFound) {
    return (
      <Page title={title} pageType={pageType}>
        <div className="migrationPageReloadFailed">Page not found.</div>
      </Page>
    );
  }

  if (migrationFallbackEnabled()) {
    return (
      <Page title={title} pageType={pageType}>
        <div className="migrationPageReloadFailed">Page has been migrated to tinuiti-frontend.</div>
      </Page>
    );
  }

  return <Page title={title} pageType={pageType} />;
};

// A route's component must keep its identity across renders: an inline `() => <MigrationPage />`
// is a new component on every parent render, so the stub remounts and re-runs the reload guard.
const migrationStubs = new Map<string, () => JSX.Element>();

export const migrationStub = (title: string): (() => JSX.Element) => {
  const stub =
    migrationStubs.get(title) ?? (() => <MigrationPage title={title} pageType={title} />);
  migrationStubs.set(title, stub);
  return stub;
};
